import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { paragon } from '@useparagon/connect';

import type { ClientConnection, IntegrationProvider } from '@polyai/schemas';
import {
  selectAllIntegrations,
  selectConnectionsByProvider,
} from '@polyai/schemas';

import { useAppSelector } from 'hooks/reduxHooks';
import { useParagonAuth } from 'providers/ParagonProvider';

// Paragon SDK authentication error names
const PARAGON_AUTH_ERROR_NAMES = [
  'UserNotAuthenticatedError',
  'HeadlessModeNotEnabledError',
] as const;

/**
 * Type guard to check if an error is a Paragon authentication error
 */
const isParagonAuthError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return PARAGON_AUTH_ERROR_NAMES.includes(
      error.name as (typeof PARAGON_AUTH_ERROR_NAMES)[number],
    );
  }
  return false;
};

export const useParagonOperation = () => {
  const { reauthenticate } = useParagonAuth();
  const allIntegrations = useAppSelector(state =>
    selectAllIntegrations(state.integrations),
  );

  const executeWithRetry = useCallback(
    async <T>(
      operation: () => Promise<T>,
      operationName = 'Paragon operation',
    ): Promise<T> => {
      try {
        // First attempt
        return await operation();
      } catch (error: unknown) {
        // Check if it's an authentication error using error.name
        if (isParagonAuthError(error)) {
          console.log(
            `${operationName} failed with auth error, attempting re-authentication...`,
          );

          const reAuthSuccess = await reauthenticate();

          if (reAuthSuccess) {
            try {
              return await operation();
            } catch (retryError) {
              console.error(
                `${operationName} failed after re-authentication:`,
                retryError,
              );
              throw retryError;
            }
          }
        }

        throw error;
      }
    },
    [reauthenticate],
  );

  const ensureAuthenticated = useCallback(async (): Promise<boolean> => {
    try {
      const user = paragon.getUser();

      if (!user?.authenticated) {
        return await reauthenticate();
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return await reauthenticate();
    }
  }, [reauthenticate]);

  const disconnectIntegration = useCallback(
    async (paragonProviderId: string, credentialsId: string) => {
      await paragon.uninstallIntegration(paragonProviderId, {
        selectedCredentialId: credentialsId,
      });
    },
    [],
  );

  const disconnectAllIntegrations = useCallback(async () => {
    const uninstalls = allIntegrations.flatMap(config =>
      Object.keys(config.connections).map(connectionId =>
        paragon
          .uninstallIntegration(config.provider, {
            selectedCredentialId: connectionId,
          })
          .catch(err => {
            console.error(
              `Failed to uninstall ${config.provider} (${connectionId}):`,
              err,
            );
          }),
      ),
    );
    await Promise.allSettled(uninstalls);
  }, [allIntegrations]);

  return {
    executeWithRetry,
    ensureAuthenticated,
    reauthenticate,
    disconnectIntegration,
    disconnectAllIntegrations,
  };
};

export function useParagonIntegrationMetadata() {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      return await executeWithRetry(
        async () => paragon.getIntegrationMetadata(),
        'Get integration metadata',
      );
    },
  });
}

export function useParagonIntegrationConfig(type: string) {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['integrationConfig', type],
    queryFn: async () => {
      return await executeWithRetry(
        async () => paragon.getIntegrationConfig(type),
        'Get integration config',
      );
    },
  });
}

export function useParagonAuthenticatedUser() {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['user'],
    queryFn: () => {
      return executeWithRetry(async () => {
        const user = paragon.getUser();
        if (!user.authenticated) {
          throw new Error('User is not authenticated');
        }
        return user;
      }, 'Get authenticated user');
    },
  });
}

type FieldOptionsResponse = Awaited<ReturnType<typeof paragon.getFieldOptions>>;

const fieldOptionsInitialData: FieldOptionsResponse = {
  data: [],
  nestedData: [],
  nextPageCursor: null,
};

export function useParagonFieldOptions({
  integration,
  sourceType,
  search,
  cursor,
  parameters = [],
  enabled = true,
}: {
  integration: string;
  sourceType: string;
  search?: string;
  cursor?: string | number | false;
  parameters?: { cacheKey: string; value: string | undefined }[];
  enabled?: boolean;
}) {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    enabled: enabled,
    queryKey: [
      'fieldOptions',
      integration,
      sourceType,
      search,
      cursor,
      parameters,
    ],
    queryFn: async () => {
      if (sourceType) {
        return await executeWithRetry(
          () =>
            paragon.getFieldOptions({
              integration,
              action: sourceType,
              search,
              cursor,
              parameters: parameters.map(parameter => {
                return {
                  key: parameter.cacheKey,
                  source: {
                    type: 'VALUE',
                    value: parameter.value,
                  },
                };
              }),
            }),
          'Get field options',
        );
      }
      return fieldOptionsInitialData;
    },
    initialData: fieldOptionsInitialData,
  });
}

export function useParagonDataSourceOptions<T>(
  integration: string,
  sourceType: string,
) {
  const { executeWithRetry } = useParagonOperation();

  return useQuery({
    queryKey: ['comboInputOptions', integration, sourceType],
    queryFn: async () => {
      return await executeWithRetry(
        () =>
          paragon.getDataSourceOptions(integration, sourceType) as Promise<T>,
        'Get data source options',
      );
    },
  });
}

/**
 * Hook to get all connections for a specific Paragon provider
 */
export const useParagonConnections = (
  provider: IntegrationProvider,
): ClientConnection[] => {
  const connections = useAppSelector(state =>
    state?.integrations
      ? selectConnectionsByProvider(state.integrations, provider)
      : [],
  );

  return connections;
};

/**
 * Hook to check if a provider has any connections
 */
export const useHasParagonConnection = (
  provider: IntegrationProvider,
): boolean => {
  const connections = useParagonConnections(provider);
  return connections.length > 0;
};
